import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("hp_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data.user))
      .catch(() => {
        localStorage.removeItem("hp_token");
        localStorage.removeItem("hp_refresh");
      })
      .finally(() => setLoading(false));
  }, []);

  const persist = (data) => {
    localStorage.setItem("hp_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("hp_refresh", data.refresh_token);
    setUser(data.user);
  };

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    if (r.data.requires_2fa)
      return { requires_2fa: true, challenge: r.data.login_challenge_token };
    persist(r.data);
    return { ok: true, user: r.data.user };
  };

  const verify2fa = async (challenge, code) => {
    const r = await api.post("/auth/2fa-verify", {
      login_challenge_token: challenge,
      code,
    });
    persist(r.data);
    return { ok: true, user: r.data.user };
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    persist(r.data);
    return { ok: true, user: r.data.user };
  };

  // One-tap, credential-free start: silently provisions an anonymous identity
  // on this device (needed so Stripe payouts can attach to the host).
  const startAnonymous = async (displayName) => {
    const rand = (
      window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)
    )
      .replace(/-/g, "")
      .slice(0, 12);
    const payload = {
      name: (displayName || "Host").trim() || "Host",
      email: `guest-${rand}@honestpay.app`,
      username: `guest_${rand}`,
      password: `Hp!${rand}Aa1`, // meets policy: upper, lower, digit, symbol, 8+
    };
    const r = await api.post("/auth/register", payload);
    persist(r.data);
    return { ok: true, user: r.data.user };
  };

  const logout = () => {
    localStorage.removeItem("hp_token");
    localStorage.removeItem("hp_refresh");
    setUser(null);
  };

  const refreshUser = async () => {
    const r = await api.get("/auth/me");
    setUser(r.data.user);
    return r.data.user;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, verify2fa, register, startAnonymous, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
